import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    AlignmentType,
    TableRow,
    TableCell,
    WidthType,
    Table,
    BorderStyle,
    ImageRun,
    Header,
    Footer,
    PageSize
} from 'docx';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

// Helper function to clean filename
const cleanFileName = (filename) => {
    const withoutUUID = filename.split('-').slice(5).join('-');
    const withoutExtension = withoutUUID.replace(/\.[^.]+$/, '');
    return withoutExtension;
};

// Helper function to load image as ArrayBuffer
const loadImage = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to load image from ${url}: ${response.statusText}`);
    }
    const blob = await response.blob();
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsArrayBuffer(blob);
    });
};

// Main function to generate claim letter
const generateClaimLetter = async (rskRef, reclamo, file = true) => {
    console.log('rskRef: ', rskRef);
    console.log('reclamo: ', reclamo);

    try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/quickview/claimsurvey?column=rsk_ref&value=${rskRef}`);
        console.log('document generate data', response.data);
        const data = response.data;

        const documentList = {
            'BL/AWB': data.quickview_claim_survey.blawb,
            'Packing List': data.quickview_claim_survey.packinglist,
            'Phyto certificate': data.quickview_claim_survey.phytocertificate,
            'DUS/DUA': data.quickview_claim_survey.dusdua,
            'Invoice': data.quickview_claim_survey.invoice,
            'Origin Certificate': data.quickview_claim_survey.origincertificate,
            'Booking': data.quickview_claim_survey.booking,
            'Booking with ETA': data.quickview_claim_survey.bookingwitheta,
            'Harvest Report': data.quickview_claim_survey.harvestreport,
            'QC at reception': data.quickview_claim_survey.qcatreception,
            'QC for finish product': data.quickview_claim_survey.qcforfinishproduct,
            'Loading/Shipping Instructions': data.quickview_claim_survey.loadingshippinginstructions,
            'Stuffing Report': data.quickview_claim_survey.stuffingreport,
            'Stuffing Report with pictures': data.quickview_claim_survey.stuffingreportwithpictures,
            'Sales results': data.quickview_claim_survey.salesresults,
            'Proof of Sales (local invoices)': data.quickview_claim_survey.proofofsaleslocalinvoices,
            'Extra expenses with support': data.quickview_claim_survey.extraexpenseswithsupport,
            'Market Prices': data.quickview_claim_survey.marketprices,
            'Credit Note': data.quickview_claim_survey.creditnote,
            'Destruction Certificate': data.quickview_claim_survey.destructioncertificate,
            'Sales Agreement': data.quickview_claim_survey.salesagreement,
            'IVV': data.quickview_claim_survey.ivv,
            'Notice of Claim': data.quickview_claim_survey.noticeofclaim,
            'QC at Destination': data.quickview_claim_survey.qcatdestination,
            'Survey Inspection': data.quickview_claim_survey.surveyinspection,
            'Loss Calculation': data.quickview_claim_survey.losscalculation,
            'LOA (exporter/insurer)': data.quickview_claim_survey.loaexporterinsurer,
            'AOR (consignee)': data.quickview_claim_survey.aorconsignee,
            'Value Protect (Maersk)': data.quickview_claim_survey.valueprotectmaersk,
            'Temp. Datalogger (Cp Peter/Hlive, etc)': data.quickview_claim_survey.meritstempdatalogger,
            'Thermograph (doors)': data.quickview_claim_survey.thermographdoors,
            'Thermograph (motor)': data.quickview_claim_survey.thermographmotor,
            'Atmosphere Datalogger (Cp Peter/Hlive, etc)': data.quickview_claim_survey.meritsgasdatalogger,
            'Gas reading at destination (curtain)': data.quickview_claim_survey.gasreadingatdestinationcurtain
        };

        // Filter documents where value is true
        const documentItems = Object.entries(documentList)
            .filter(([_, value]) => value === true)
            .map(([key], index) => ({
                number: index + 1,
                name: key,
            }));

        // Split documents into two columns
        const halfLength = Math.ceil(documentItems.length / 2);
        const leftColumn = documentItems.slice(0, halfLength);
        const rightColumn = documentItems.slice(halfLength);

        // Create table rows for documents
        const rows = leftColumn.map((leftItem, index) => {
            const rightItem = rightColumn[index] || null;
            return new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({
                            children: [new TextRun(`${leftItem.number}. ${leftItem.name}`)],
                        })],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                    new TableCell({
                        children: rightItem ? [new Paragraph({
                            children: [new TextRun(`${rightItem.number}. ${rightItem.name}`)],
                        })] : [],
                        width: { size: 50, type: WidthType.PERCENTAGE },
                    }),
                ],
            });
        });

        // Define breakdown data
        const insurancePayment = [
            {
                icon: 'lucide:dollar-sign',
                label: 'Insurance Payment',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.insurancepayment || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                label: 'Deductible',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.deductible || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                label: 'Not Covered',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.not_covered || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Claim Amount',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.claimamount || 0)
            },
        ];

        // Definir perdidaCalculadaConsignment
        const perdidaCalculadaConsignment = [
            {
                icon: 'lucide:dollar-sign',
                label: 'Sound Market Value (SMV)',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.consign_sound_market_value || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:minus',
                label: 'Actual Sales Value',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.consign_actual_sales_value || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Loss Due to Damage',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.consign_loss_due_damage || 0)
            },
            ...(data?.extra_costs?.length > 0
                ? data.extra_costs.map((item) => ({
                    icon: 'lucide:dollar-sign',
                    icon2: 'lucide:plus',
                    label: item.name || 'Extra Cost',
                    value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        .format(parseFloat(item.value) || 0)
                }))
                : [
                    {
                        icon: 'lucide:dollar-sign',
                        icon2: 'lucide:plus',
                        label: 'Extra Costs Incurred (if any)',
                        value: '0.00'
                    }
                ]),
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Claim Amount',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.claimamount || 0)
            },
        ];

        // Definir perdidaCalculadaFirm
        const perdidaCalculadaFirm = [
            {
                icon: 'lucide:dollar-sign',
                label: 'Invoice Value',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.firm_invoice_value || 0)
            },
            {
                icon: "lucide:dollar-sign",
                icon2: "lucide:minus",
                label: "Sales",
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.firm_sales || 0),
                show: data?.quickview_claim_survey.firm_sales != null
            },
            {
                icon: "lucide:dollar-sign",
                label: "Credit Note",
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.firm_credit_note || 0),
                show: data?.quickview_claim_survey.firm_credit_note != null
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:plus',
                label: 'Freight',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.firm_freight || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Loss Due Damage',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.firm_loss_due_damage || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:plus',
                label: 'Destruction if Apply',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.firm_destruction_if_apply || 0)
            },
            ...(data?.extra_costs?.length > 0
                ? data.extra_costs.map((item) => ({
                    icon: 'lucide:dollar-sign',
                    icon2: 'lucide:plus',
                    label: item.name || 'Extra Expense',
                    value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        .format(parseFloat(item.value) || 0)
                }))
                : [
                    {
                        icon: 'lucide:dollar-sign',
                        icon2: 'lucide:plus',
                        label: 'Other Extra Expenses',
                        value: '0.00'
                    }
                ]),
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Claim Amount',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.claimamount || 0)
            },
        ].filter(field => field.show !== false);

        // Definir perdidaCalculadaMG

        const perdidaCalculadaMG = [
            {
                icon: 'lucide:dollar-sign',
                label: 'Sound Market Value (SMV)',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.mg_sound_market_value || 0),
                show: data?.quickview_claim_survey.mg_sound_market_value != null
            },
            {
                icon: 'lucide:dollar-sign',
                label: 'Invoice Value',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.mg_invoice_value || 0),
                show: data?.quickview_claim_survey.mg_invoice_value != null
            },
            {
                icon: "lucide:dollar-sign",
                icon2: "lucide:minus",
                label: "Sales",
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.mg_sales || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Loss Due Damage',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.mg_loss_due_damage || 0)
            },
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:plus',
                label: 'Destruction if Apply',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.mg_destruction_if_apply || 0)
            },
            ...(data?.extra_costs?.length > 0
                ? data.extra_costs.map((item) => ({
                    icon: 'lucide:dollar-sign',
                    icon2: 'lucide:plus',
                    label: item.name || 'Extra Expense',
                    value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        .format(parseFloat(item.value) || 0)
                }))
                : [
                    {
                        icon: 'lucide:dollar-sign',
                        icon2: 'lucide:plus',
                        label: 'Other Extra Expenses',
                        value: '0.00'
                    }
                ]),
            {
                icon: 'lucide:dollar-sign',
                icon2: 'lucide:equal',
                label: 'Claim Amount',
                value: new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    .format(data?.quickview_claim_survey.claimamount || 0)
            },
        ].filter(field => field.show !== false);

        // Determine which breakdown to use based on conditions
        let breakdownTitle = '';
        let breakdownItems = [];

        if (
            data?.quickview_claim_survey.insurancepayment != null &&
            data?.quickview_claim_survey.insurancepayment !== '' &&
            data?.quickview_claim_survey.deductible != null &&
            data?.quickview_claim_survey.deductible !== '' &&
            data?.quickview_claim_survey.not_covered != null &&
            data?.quickview_claim_survey.not_covered !== ''
        ) {
            breakdownTitle = 'Insurance Payment';
            breakdownItems = insurancePayment;
        } else if (
            (data?.quickview_claim_survey.insurancepayment == null || data?.quickview_claim_survey.insurancepayment === '') &&
            (data?.quickview_claim_survey.deductible == null || data?.quickview_claim_survey.deductible === '') &&
            (data?.quickview_claim_survey.not_covered == null || data?.quickview_claim_survey.not_covered === '') &&
            data?.quickview_claim_survey.selected_calculation === 'consignment'
        ) {
            breakdownTitle = 'Loss Calculation: Free Consignment';
            breakdownItems = perdidaCalculadaConsignment;
        } else if (
            (data?.quickview_claim_survey.insurancepayment == null || data?.quickview_claim_survey.insurancepayment === '') &&
            (data?.quickview_claim_survey.deductible == null || data?.quickview_claim_survey.deductible === '') &&
            (data?.quickview_claim_survey.not_covered == null || data?.quickview_claim_survey.not_covered === '') &&
            data?.quickview_claim_survey.selected_calculation === 'firm'
        ) {
            breakdownTitle = 'Loss Calculation: Firm';
            breakdownItems = perdidaCalculadaFirm;
        } else if (
            (data?.quickview_claim_survey.insurancepayment == null || data?.quickview_claim_survey.insurancepayment === '') &&
            (data?.quickview_claim_survey.deductible == null || data?.quickview_claim_survey.deductible === '') &&
            (data?.quickview_claim_survey.not_covered == null || data?.quickview_claim_survey.not_covered === '') &&
            data?.quickview_claim_survey.selected_calculation === 'mg'
        ) {
            breakdownTitle = 'Loss Calculation: Minimum Guaranteed (MG)';
            breakdownItems = perdidaCalculadaMG;
        } else {
            breakdownTitle = 'Breakdown';
            breakdownItems = [
                { label: 'No breakdown available', value: 'N/A' }
            ];
        }

        // Claim data
        const claimData = {
            date: `${reclamo.created ? format(toZonedTime(new Date(reclamo.created), 'UTC'), 'EEEE, MMMM do, yyyy') : 'N/A'}`,
            claimRef: data.quickview_claim?.rsk_ref || 'N/A',
            carrier_name: data.quickview_claim?.carrier_name || 'N/A',
            uniqueCode: reclamo.unique_code || 'N/A',
            bl: data.quickview_claim?.bl_awb || 'N/A',
            vessel: data.quickview_claim?.vessel || 'N/A',
            pol: data.quickview_claim?.pol || 'N/A',
            etd: `${data.box?.etd ? format(toZonedTime(new Date(data.box?.etd), 'UTC'), 'dd-MM-yyyy') : 'N/A'}`,
            ata: `${data.quickview_claim?.ata ? format(toZonedTime(new Date(data.quickview_claim?.ata), 'UTC'), 'dd-MM-yyyy') : 'N/A'}`,
            claimAmount: `${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                .format(data.quickview_claim_survey?.claimamount || 0)} ${data.quickview_claim_survey?.selectedcurrency || 'N/A'}`,

            container: data.quickview_claim?.container_number || 'N/A',
            pod: data.quickview_claim?.pod || 'N/A',
            eta: `${data.quickview_claim?.eta ? format(toZonedTime(new Date(data.quickview_claim?.eta), 'UTC'), 'dd-MM-yyyy') : 'N/A'}`,
            goods: data.maestra?.especie || 'N/A',
            principals: data.quickview_claim?.exporter_name || 'N/A',
            valueprotect: `${data.quickview_claim_survey?.valueprotectmaersk ? 'Yes' : 'No'}`,
            voyage: data.maestra?.voyage_number || 'N/A',
        };

        // Crear tabla de claim data
        const claimTable = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE },
                insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            width: { size: 50, type: WidthType.PERCENTAGE },
                            children: [
                                new Paragraph({ children: [
                                        new TextRun({ text: `B/L\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.bl}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `VESSEL\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.vessel}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `POL\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.pol}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `ETD\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.etd}` })
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `ATA\t\t\t`, bold: true}),
                                        new TextRun({ text: `${claimData.ata}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `CLAIM AMOUNT\t`, bold: true}),
                                        new TextRun({ text: `${claimData.claimAmount}`})
                                    ] }),
                            ],
                        }),
                        new TableCell({
                            width: { size: 50, type: WidthType.PERCENTAGE },
                            children: [
                                new Paragraph({ children: [
                                        new TextRun({ text: `CONTAINER\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.container}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `VOYAGE\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.voyage}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `POD\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.pod}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `ETA\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.eta}`})
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `GOODS\t\t\t`, bold: true }),
                                        new TextRun({ text: `${claimData.goods}` })
                                    ] }),
                                new Paragraph({ children: [
                                        new TextRun({ text: `VALUE PROTECT\t`, bold: true }),
                                        new TextRun({ text: `${claimData.valueprotect}` })
                                    ] }),
                            ],
                        }),
                    ],
                }),
            ],
        });

        // Crear tabla para el desglose (breakdown)
        const breakdownRows = breakdownItems.flatMap((item, index) => {
            const operator = item.icon2
                ? item.icon2 === 'lucide:minus' ? '-'
                    : item.icon2 === 'lucide:plus' ? '+'
                        : item.icon2 === 'lucide:equal' ? '='
                            : ''
                : '';

            // Determinar si se debe agregar una línea horizontal antes del "Claim Amount"
            const isBeforeClaimAmount = index < breakdownItems.length - 1 && // No es el último ítem
                breakdownItems[index + 1].label === 'Claim Amount'; // El siguiente ítem es el Claim Amount

            // Fila para el valor y la etiqueta
            const valueRow = new TableRow({
                children: [
                    new TableCell({
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.RIGHT,
                                children: [
                                    new TextRun({
                                        text: `${operator ? `${operator} ` : ''}${item.value} ${data.quickview_claim_survey?.selectedcurrency || ''}`,
                                        bold: item.icon2 === 'lucide:equal',
                                    }),
                                ],
                            }),
                        ],
                    }),
                    new TableCell({
                        width: { size: 60, type: WidthType.PERCENTAGE },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.LEFT,
                                children: [
                                    new TextRun({
                                        text: `(${item.label})`,
                                        bold: item.icon2 === 'lucide:equal',
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            });

            // Fila para la línea horizontal (si aplica)
            const lineRow = isBeforeClaimAmount
                ? [
                    new TableRow({
                        children: [
                            new TableCell({
                                columnSpan: 2, // Ocupa ambas columnas
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            new TextRun({
                                                text: '____________________',
                                                size: 24,
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                        ],
                    }),
                ]
                : [];

            return [valueRow, ...lineRow];
        });

        const breakdownTable = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE },
                insideVertical: { style: BorderStyle.NONE },
            },
            rows: breakdownRows,
        });

        // Cargar imágenes
        const headerImageData = await loadImage('/carta/headerv2.png');
        const footerImageData = await loadImage('/carta/cuadradosv2.png');
        const rsk_group_logo_buffer = await loadImage('/carta/rsk_groupv2.png');
        const fernanda_signature = await loadImage('/carta/Fernandadelmarv2.png');
        const quickviewlogo = await loadImage('/carta/QuickViewR.png');

        // Crear documento
        const doc = new Document({
            styles: {
                default: {
                    document: {
                        run: {
                            font: 'Cambria',
                            color: '595959',
                        },
                    },
                },
            },

            sections: [
                {
                    headers: {
                        default: new Header({
                            children: [
                                new Table({
                                    borders: {
                                        top: { style: BorderStyle.NONE },
                                        bottom: { style: BorderStyle.NONE },
                                        left: { style: BorderStyle.NONE },
                                        right: { style: BorderStyle.NONE },
                                        insideHorizontal: { style: BorderStyle.NONE },
                                        insideVertical: { style: BorderStyle.NONE },
                                    },
                                    width: {
                                        size: 100,
                                        type: WidthType.PERCENTAGE,
                                    },
                                    rows: [
                                        new TableRow({
                                            children: [
                                                new TableCell({
                                                    width: {
                                                        size: 50,
                                                        type: WidthType.PERCENTAGE,
                                                    },
                                                    children: [
                                                        new Paragraph({
                                                            alignment: AlignmentType.LEFT,
                                                            children: [
                                                                new ImageRun({
                                                                    type: "png",
                                                                    data: headerImageData,
                                                                    transformation: {
                                                                        width: 80,
                                                                        height: 50,
                                                                    },
                                                                }),
                                                            ],
                                                        }),
                                                    ],
                                                }),
                                                new TableCell({
                                                    width: {
                                                        size: 50,
                                                        type: WidthType.PERCENTAGE,
                                                    },
                                                    children: [
                                                        new Paragraph({
                                                            alignment: AlignmentType.RIGHT,
                                                            children: [
                                                                new ImageRun({
                                                                    type: "png",
                                                                    data: quickviewlogo,
                                                                    transformation: {
                                                                        width: 130,
                                                                        height: 20,
                                                                    },
                                                                }),
                                                            ],
                                                        }),
                                                    ],
                                                }),
                                            ],
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    },
                    footers: {
                        default: new Footer({
                            children: [
                                new Paragraph({
                                    children: [
                                        new ImageRun({
                                            type: 'png',
                                            data: footerImageData,
                                            transformation: {
                                                width: 115,
                                                height: 25,
                                            },
                                        }),
                                    ],
                                }),
                            ],
                        }),
                    },
                    properties: {
                        page: {
                            size: {
                                orientation: "portrait",
                                width: 12240,
                                height: 20160,
                            },
                            margin: {
                                top: 720,
                                bottom: 720,
                                left: 720,
                                right: 720,
                            },
                            gutter: 0,
                            gutterPosition: 'left',
                        },
                    },
                    children: [
                        new Paragraph({
                            alignment: AlignmentType.RIGHT,
                            children: [new TextRun({ text: claimData.date, italics: true, size: 20 })],
                        }),
                        new Paragraph({ children: [new TextRun({ text: 'Messrs.', italics: true, size: 22 })] }),
                        new Paragraph({ children: [new TextRun({ text: `${claimData.carrier_name}`, bold: true, italics: true, size: 22 })] }),
                        new Paragraph({
                            children: [new TextRun({
                                text: 'Claims Department.',
                                bold: true,
                                italics: true,
                                size: 22
                            })]
                        }),
                        new Paragraph({
                            children: [],
                            border: {
                                top: {
                                    style: BorderStyle.SINGLE,
                                    size: 4,
                                    color: 'BFBFBF',
                                },
                            },
                            spacing: {
                                before: 50,
                                after: 50,
                            },
                        }),
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [new TextRun({
                                text: `Claim Letter Ref. N° ${claimData.claimRef}`,
                                bold: true,
                                size: 24
                            })],
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 24})] }),
                        claimTable,
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new TextRun(
                                    `By this means and on behalf of our principals Messrs. ${claimData.principals}, we hereby claim and request the reimbursement of ${claimData.claimAmount}. -, since the shipment was exposed to abnormal transit conditions while being transported under carrier's custody.`
                                ),
                            ],
                        }),
                        new Paragraph({
                            children: [],
                            border: {
                                bottom: {
                                    style: BorderStyle.SINGLE,
                                    size: 4,
                                    color: 'BFBFBF',
                                },
                            },
                            spacing: {
                                before: 50,
                                after: 50,
                            },
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: 'For analysis of this claim and reimbursement request, please use the unique code in the following exclusive access for carriers:',
                                    bold: true,
                                }),
                            ],
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: 'https://www.rskgroupco.com',
                                    link: 'https://www.rskgroupco.com',
                                    size: 24,
                                }),
                                new TextRun({
                                    text: '         ',
                                    size: 24,
                                }),
                                new TextRun({
                                    text: `Unique Code: ${claimData.uniqueCode}`,
                                    bold: true,
                                    size: 24,
                                }),
                            ],
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 12})] }),
                        new Table({
                            rows: rows,
                            width: { size: 100, type: WidthType.PERCENTAGE },
                            borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE },
                                insideHorizontal: { style: BorderStyle.NONE },
                                insideVertical: { style: BorderStyle.NONE },
                            },
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        // Sección de desglose (breakdown) con tabla
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: breakdownTitle.toUpperCase(),
                                    bold: true,
                                }),
                            ],
                        }),
                        breakdownTable,
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new TextRun(
                                    'This claim and reimbursement request stops any statute of limitation, producing legal interest from the date of the sinister to the verification of payment.'
                                ),
                            ],
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: 'RSK Group',
                                    bold: true,
                                }),
                                new TextRun(
                                    ` is duly acting on behalf of ${claimData.principals}, and we would appreciate your prompt attention to this matter to try to find a mutually agreed solution.`
                                ),
                            ],
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [new TextRun({ text: 'Kind regards,', bold: true })]
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new ImageRun({
                                    type: 'png',
                                    data: rsk_group_logo_buffer,
                                    transformation: {
                                        width: 118,
                                        height: 20,
                                    },
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({ children: [new TextRun({text: '', size: 20})] }),
                        new Paragraph({
                            children: [
                                new ImageRun({
                                    type: 'png',
                                    data: fernanda_signature,
                                    transformation: {
                                        width: 278,
                                        height: 40,
                                    },
                                }),
                            ],
                            alignment: AlignmentType.CENTER,
                        }),
                    ],
                },
            ],
        });

        // Generar y descargar el documento
        const blob = await Packer.toBlob(doc);

        if (file){
            saveAs(blob, `Claim_Letter_${claimData.claimRef}.docx`);
        } else {
            return { blob, filename: `Claim_Letter_${claimData.claimRef}.docx` };
        }

    } catch (error) {
        console.error('Error al obtener los datos o generar el documento:', error);
        throw error;
    }
};

// Export the function
export default generateClaimLetter;